import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildSorter,
  CurrentUserOrganizationDto,
  useInfiniteOrganizations,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { useNotification } from '@sib/notifications';
import { useDebouncedValue } from '@sib/utils';

const PAGE_SIZE = 100;

export interface OrganizationSwitchDialogProps {
  onClose: () => void;
  open: boolean;
}

export const useOrganizationSwitch = ({
  onClose,
}: OrganizationSwitchDialogProps) => {
  const {
    organizations,
    organizationId,
    organizationName,
    setOrganizationId,
    setOrganizationName,
    setEntryPointOrganization,
  } = useUserSetupStore();
  const [selectedOrganization, setSelectedOrganization] =
    useState<CurrentUserOrganizationDto | null>(null);
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const { open: showNotification } = useNotification();

  const {
    data: userOrganizations,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteOrganizations({
    page: 1,
    size: PAGE_SIZE,
    search: debouncedSearch,
    sort: buildSorter({
      field: 'name',
      order: 'asc',
    }),
  });

  const filteredOrganizations = useMemo(
    () => userOrganizations?.pages.map(page => page.items).flat() || [],
    [userOrganizations]
  );

  const totalRows = useMemo(
    () => userOrganizations?.pages[0].totalItems || 0,
    [userOrganizations]
  );

  const defaultOrganization = useMemo(
    () => ({ id: organizationId, name: organizationName }),
    [organizationId, organizationName]
  );

  const onSubmit = useCallback(() => {
    setOrganizationId(selectedOrganization?.id || '');
    setOrganizationName(selectedOrganization?.name || '');
    setEntryPointOrganization(null);
    onClose();
    showNotification({
      message: t('components.organizationSwitchDialog.successMessage', {
        organization: defaultOrganization?.name,
        newOrganization: selectedOrganization?.name,
        interpolation: { escapeValue: false },
      }),
      type: 'success',
    });
  }, [
    onClose,
    selectedOrganization,
    setOrganizationId,
    showNotification,
    setOrganizationName,
    t,
    defaultOrganization,
    setEntryPointOrganization,
  ]);

  const onDialogClose = useCallback(() => {
    onClose();
    setSearch('');
    setSelectedOrganization(defaultOrganization);
  }, [onClose, defaultOrganization]);

  useEffect(() => {
    if (!defaultOrganization) return;
    setSelectedOrganization(defaultOrganization);
  }, [defaultOrganization]);

  return {
    onSearch: setSearch,
    organizations,
    filteredOrganizations,
    onSubmit,
    defaultOrganization,
    search,
    selectedOrganization,
    onDialogClose,
    setSelectedOrganization,
    isLoading: isFetching && !isFetchingNextPage,
    infiniteList: {
      fetchNextPage,
      totalRows,
      hasMore: hasNextPage,
      dataLength: filteredOrganizations.length,
    },
  };
};
